import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Button, Card, Form, Input, message, Modal, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, KeyOutlined, DatabaseOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../utils/api';

const { Title, Paragraph, Text } = Typography;

const maskSecret = (value) => {
  if (!value) return '';
  if (value.length <= 4) return '*'.repeat(value.length);
  const visible = value.slice(-4);
  return `${'*'.repeat(Math.max(value.length - 4, 0))}${visible}`;
};

const GeoTabSettings = () => {
  const [form] = Form.useForm();
  const [testForm] = Form.useForm();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [filterDatabase, setFilterDatabase] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const initialValues = useMemo(() => ({
    base_url: 'https://my.geotab.com',
  }), []);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/integrations/geotab/accounts');
      const withMeta = (Array.isArray(res) ? res : []).map((acc) => ({
        ...acc,
        maskedPassword: maskSecret(acc.password || ''),
        maskedSession: maskSecret(acc.session_id || ''),
      }));
      setAccounts(withMeta);
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.error || 'Failed to load Geotab accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const handleEdit = (record) => {
    setEditingAccount(record);
    form.setFieldsValue({
      name: record.name || '',
      base_url: record.base_url || 'https://my.geotab.com',
      database: record.database,
      username: record.username,
      password: '',
      session_id: record.session_id || '',
    });
    testForm.setFieldsValue({
      base_url: record.base_url || 'https://my.geotab.com',
      database: record.database,
      username: record.username,
      password: '',
      session_id: record.session_id || '',
    });
  };

  const resetForm = useCallback(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    setEditingAccount(null);
    testForm.resetFields();
    testForm.setFieldsValue({ base_url: initialValues.base_url });
  }, [form, testForm, initialValues]);

  useEffect(() => {
    resetForm();
  }, [initialValues, testForm, resetForm]);

  const onFinish = async (values) => {
    try {
      setSaving(true);
      if (editingAccount) {
        const payload = {
          ...values,
          password: values.password ? values.password : undefined,
          session_id: values.session_id || null,
        };
        await api.put(`/integrations/geotab/accounts/${editingAccount.id}`, payload);
        message.success('Geotab account updated');
      } else {
        await api.post('/integrations/geotab/accounts', values);
        message.success('Geotab account created');
      }
      await loadAccounts();
      resetForm();
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.error || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      setDeletingId(id);
      await api.delete(`/integrations/geotab/accounts/${id}`);
      message.success('Geotab account deleted');
      await loadAccounts();
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.error || 'Failed to delete account');
    } finally {
      setDeletingId(null);
    }
  };

  const openTestModal = (record) => {
    setEditingAccount(record);
    setTestResult(null);
    testForm.setFieldsValue({
      base_url: record?.base_url || 'https://my.geotab.com',
      database: record?.database,
      username: record?.username,
      password: '',
      session_id: record?.session_id || '',
    });
    setTestModalVisible(true);
  };

  const testConnection = async () => {
    try {
      setTestLoading(true);
      const values = await testForm.validateFields();
      const body = editingAccount?.id
        ? { accountId: editingAccount.id }
        : {
            base_url: values.base_url,
            userName: values.username,
            password: values.password,
            database: values.database,
          };
      const result = await api.post('/integrations/geotab/authenticate', body);
      setTestResult({ success: true, result });
      message.success('Authentication succeeded');
    } catch (err) {
      if (err?.errorFields) return;
      console.error(err);
      setTestResult({ success: false, error: err?.response?.data?.error || err?.message || 'Unknown error' });
      message.error(err?.response?.data?.error || 'Authentication failed');
    } finally {
      setTestLoading(false);
    }
  };

  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      const matchesDb = !filterDatabase || (acc.database || '').toLowerCase().includes(filterDatabase.toLowerCase());
      const hasSession = acc.session_id ? 'connected' : 'not_connected';
      const matchesStatus = filterStatus === 'all' || filterStatus === hasSession;
      return matchesDb && matchesStatus;
    });
  }, [accounts, filterDatabase, filterStatus]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (val, record) => val || `${record.username || ''}`,
    },
    {
      title: 'Database',
      dataIndex: 'database',
      key: 'database',
      render: (val) => <Text code>{val}</Text>,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Session',
      dataIndex: 'session_id',
      key: 'session_id',
      render: (val) => (
        val
          ? <Tag color="green">Connected</Tag>
          : <Tag color="orange">Requires login</Tag>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (val) => (val ? dayjs(val).format('M/D/YYYY h:mm A') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          <Button size="small" icon={<KeyOutlined />} onClick={() => openTestModal(record)}>Test</Button>
          <Popconfirm
            title="Delete this Geotab account?"
            okText="Delete"
            okButtonProps={{ danger: true, loading: deletingId === record.id }}
            onConfirm={() => onDelete(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} loading={deletingId === record.id}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Settings' }, { title: 'GeoTab Settings' }]} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>GeoTab Settings</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadAccounts} loading={loading}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => resetForm()}>New Credential</Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 24 }}>
        <Paragraph type="secondary">
          Manage your GeoTab database credentials below. Each entry allows this workspace to fetch GPS data from a specific GeoTab database.
        </Paragraph>

        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          onFinish={onFinish}
        >
          <Form.Item label="Label" name="name" tooltip="Optional friendly name">
            <Input placeholder="My Fleet Credentials" />
          </Form.Item>

          <Form.Item label="Base URL" name="base_url" rules={[{ required: true, message: 'Base URL is required' }]}
            tooltip="GeoTab server base URL (default https://my.geotab.com)">
            <Input placeholder="https://my.geotab.com" />
          </Form.Item>

          <Form.Item label="Database" name="database" rules={[{ required: true, message: 'Database name is required' }]}
            tooltip="GeoTab database name (e.g., demo, pvrentals)">
            <Input placeholder="Database name" />
          </Form.Item>

          <Form.Item label="Username" name="username" rules={[{ required: true, message: 'Username is required' }]}>
            <Input placeholder="GeoTab username" />
          </Form.Item>

          <Form.Item label="Password" name="password" tooltip="Leave blank to keep current password">
            <Input.Password
              placeholder="GeoTab password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item label="Session ID" name="session_id" tooltip="Optional: paste an active GeoTab session ID">
            <Input placeholder="Existing session ID (optional)" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <Space>
              {editingAccount && <Tag color="blue">Editing existing credential</Tag>}
            </Space>
            <Space>
              <Button onClick={resetForm}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving}>{editingAccount ? 'Update' : 'Add'}</Button>
            </Space>
          </div>
        </Form>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <Space>
            <Input
              allowClear
              placeholder="Filter by database"
              value={filterDatabase}
              onChange={(e) => setFilterDatabase(e.target.value)}
              prefix={<DatabaseOutlined />}
              style={{ width: 220 }}
            />
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'connected', label: 'Connected' },
                { value: 'not_connected', label: 'Requires Login' },
              ]}
              style={{ width: 180 }}
            />
          </Space>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredAccounts}
          columns={columns}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="Test GeoTab Credentials"
        open={testModalVisible}
        onCancel={() => { setTestModalVisible(false); setTestResult(null); }}
        footer={null}
      >
        <Paragraph>
          We will attempt to authenticate with the provided credentials. If successful, a session ID will be returned and stored for this account.
        </Paragraph>
        <Form layout="vertical" form={testForm} onFinish={testConnection}>
          <Form.Item label="Base URL" name="base_url" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Database" name="database" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Username" name="username" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Password" name="password" rules={[{ required: editingAccount ? false : true, message: 'Password is required unless using stored session' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item label="Session ID" name="session_id">
            <Input placeholder="Optional existing session" />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setTestModalVisible(false)}>Close</Button>
            <Button type="primary" htmlType="submit" loading={testLoading}>Run Test</Button>
          </div>
        </Form>
        {testResult && (
          <Card size="small" style={{ marginTop: 12 }}>
            {testResult.success ? (
              <div>
                <Paragraph strong type="success">Authentication successful.</Paragraph>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(testResult.result, null, 2)}</pre>
              </div>
            ) : (
              <Paragraph type="danger">{testResult.error}</Paragraph>
            )}
          </Card>
        )}
      </Modal>
    </div>
  );
};

export default GeoTabSettings;
